import { Break } from '@ps/isp-web-component-library/icons/Break';
import { Finished } from '@ps/isp-web-component-library/icons/Finished';
import { InPlay } from '@ps/isp-web-component-library/icons/InPlay';
import { Translation } from '@ps/web-shared-components';
import type { FootballFixture } from '@ps/web-shared-generated-graphql/sports';
import React from 'react';

import EventStatusMessage from '../../EventStatusMessage/EventStatusMessage';
import GameTime from '../GameTime/GameTime';

import { TXTWCL_Extra_time___N0 } from './FootballScoreboardInPlayStatus.messages.json';

interface Props {
  footballFixture: FootballFixture;
}

const FootballScoreboardInPlayStatus: React.FC<Props> = ({ footballFixture }) => {
  const { score, duration, penaltyShootout, status } = footballFixture;
  if (status === 'SUSPENDED') {
    return <EventStatusMessage messageId="PLAY_SUSPENDED" icon={Break} size="XSmall" />;
  }

  if (status === 'POSTPONED') {
    return <EventStatusMessage messageId="POSTPONED" icon={Finished} size="XSmall" />;
  }

  if (!score) {
    return <EventStatusMessage messageId="IN_PLAY" icon={InPlay} size="XSmall" />;
  }

  if (penaltyShootout) {
    return <EventStatusMessage messageId="PENALTIES" icon={InPlay} size="XSmall" />;
  }

  if (!duration) {
    return <EventStatusMessage messageId="IN_PLAY" icon={InPlay} size="XSmall" />;
  }

  switch (duration.status) {
    case 'INPLAY_FIRST_HALF':
    case 'INPLAY_SECOND_HALF': {
      if (duration.period === 'EXTRA') {
        if (duration.clock) {
          const fullHalfMinutes = duration.status === 'INPLAY_FIRST_HALF' ? 105 : 120;
          if (duration.clock.roundMinute > fullHalfMinutes) {
            return (
              <GameTime>
                <Translation
                  {...TXTWCL_Extra_time___N0}
                  values={{
                    0: `${fullHalfMinutes}’ +${duration.clock.roundMinute - fullHalfMinutes}`
                  }}
                />
              </GameTime>
            );
          }

          return (
            <GameTime>
              <Translation
                {...TXTWCL_Extra_time___N0}
                values={{
                  0: `${duration.clock.roundMinute}’`
                }}
              />
            </GameTime>
          );
        }

        return <EventStatusMessage messageId="EXTRA_TIME" icon={InPlay} size="XSmall" />;
      }

      if (duration.clock) {
        const fullHalfMinutes = duration.status === 'INPLAY_FIRST_HALF' ? 45 : 90;
        if (duration.clock.roundMinute > fullHalfMinutes) {
          return (
            <GameTime>
              <span>{`${fullHalfMinutes}’ +${duration.clock.roundMinute - fullHalfMinutes}`}</span>
            </GameTime>
          );
        }
        return (
          <GameTime>
            <span>{`${duration.clock.roundMinute}’`}</span>
          </GameTime>
        );
      }

      return <EventStatusMessage messageId="IN_PLAY" icon={InPlay} size="XSmall" />;
    }
    case 'HALF': {
      if (duration.period === 'EXTRA') {
        return <EventStatusMessage messageId="EXTRA_TIME_HALF_TIME" icon={InPlay} size="XSmall" />;
      }
      return <EventStatusMessage messageId="HALF_TIME" icon={InPlay} size="XSmall" />;
    }
    case 'FULL':
    case 'END':
      return <EventStatusMessage messageId="FULL_TIME" icon={Finished} size="XSmall" />;
    default:
      return <EventStatusMessage messageId="IN_PLAY" icon={InPlay} size="XSmall" />;
  }
};

export default FootballScoreboardInPlayStatus;
